import axios from 'axios';
import { extractApiError } from '../utils/apiError';
import { responseTransformer, isEnvelopeFormat } from '../utils/apiResponse';
import { authEvents, AUTH_EVENTS } from '../utils/authEvents';

// ============ API Configuration ============

// API Version - all new endpoints use v1
const API_VERSION = 'v1';

// Base URLs
// Normalize to ensure we always target /api/sat/algo even if REACT_APP_API_URL is set to /api
const configuredApiUrl = process.env.REACT_APP_API_URL || '/api';
const API_URL = configuredApiUrl.endsWith('/sat/algo')
  ? configuredApiUrl
  : `${configuredApiUrl.replace(/\/$/, '')}/sat/algo`;

const VERSIONED_API_URL = `${API_URL}/${API_VERSION}`;
// Legacy API URL removed - all endpoints now use versioned API

// Create versioned axios instance (for new v1 endpoints)
const api = axios.create({
  baseURL: VERSIONED_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============ Request Interceptor ============

/**
 * Add auth token to all requests
 */
const addAuthInterceptor = (instance) => {
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
};

// ============ Response Interceptor ============

/**
 * Handle response unwrapping and error normalization
 */
const addResponseInterceptor = (instance) => {
  instance.interceptors.response.use(
    (response) => {
      // Check if response follows the new envelope format
      if (isEnvelopeFormat(response.data)) {
        // Use the transformer to unwrap the response
        try {
          return responseTransformer(response);
        } catch (error) {
          // If transformer throws (e.g., success=false), reject with ApiError
          return Promise.reject(error);
        }
      }
      // Legacy response - return as-is
      return response;
    },
    (error) => {
      // Extract and normalize the error
      const apiError = extractApiError(error);

      // Handle 401 Unauthorized - emit event for AuthContext to handle
      if (apiError.isAuthError()) {
        // Emit event for AuthContext to clear state
        authEvents.emit(AUTH_EVENTS.UNAUTHORIZED);

        // Also clear localStorage as a safety measure
        localStorage.removeItem('token');
        localStorage.removeItem('userEmail');

        // Redirect to signin for protected routes
        const protectedPaths = ['/dashboard/orders', '/dashboard/brokers', '/dashboard/strategies', '/dashboard/live-trading'];
        if (protectedPaths.some(path => window.location.pathname.startsWith(path))) {
          window.location.href = '/signin';
        }
      }

      // Log error for debugging (in development)
      if (process.env.NODE_ENV === 'development') {
        console.error('API Error:', apiError.toLogObject());
      }

      return Promise.reject(apiError);
    }
  );
};

// Apply interceptors to main api instance
addAuthInterceptor(api);
addResponseInterceptor(api);

// ============ Auth Service ============
// Base: /api/sat/algo/v1/auth

export const authService = {
  /**
   * Login user
   * POST /api/sat/algo/v1/auth/login
   */
  login: (credentials) => api.post('/auth/login', credentials),

  /**
   * Signup new user
   * POST /api/sat/algo/v1/auth/signup
   */
  signup: (userData) => api.post('/auth/signup', userData),

  /**
   * Request password reset
   * POST /api/sat/algo/v1/auth/forgot-password
   */
  forgotPassword: (data) => api.post('/auth/forgot-password', data),

  /**
   * Validate reset token
   * GET /api/sat/algo/v1/auth/validate-reset-token?token={token}
   */
  validateResetToken: (token) => api.get('/auth/validate-reset-token', { params: { token } }),

  /**
   * Reset password with token
   * POST /api/sat/algo/v1/auth/reset-password
   */
  resetPassword: (data) => api.post('/auth/reset-password', data),

  /**
   * Initiate Google OAuth login
   * Redirects to Google OAuth consent screen
   */
  googleLogin: () => {
    window.location.href = `${VERSIONED_API_URL}/auth/google/login`;
  },

  // Aliases for backward compatibility
  get loginUser() { return this.login; },
  get signupUser() { return this.signup; },
};

// ============ TrendLine Strategy Service ============
// Base: /api/sat/algo/v1/strategies/trendline

const TRENDLINE_STRATEGY_BASE = '/strategies/trendline';

export const trendlineStrategyService = {
  // ============ CRUD Operations ============

  /**
   * Get all trendline strategies
   * GET /api/sat/algo/v1/strategies/trendline
   */
  getAll: () => api.get(TRENDLINE_STRATEGY_BASE),

  /**
   * Search strategies with filters and pagination
   * GET /api/sat/algo/v1/strategies/search
   * @param {Object} params
   * @param {string} [params.strategyId] - Exact strategy ID match
   * @param {string} [params.name] - Case-insensitive partial name match
   * @param {boolean} [params.isPublic] - Visibility filter
   * @param {number} [params.page=0] - Page number (zero-indexed)
   * @param {number} [params.size=20] - Page size
   * @returns {Object} { data: [...strategies], meta: { page, pageSize, totalPages, totalItems } }
   */
  searchStrategies: ({ strategyId, name, isPublic, page = 0, size = 20, sort } = {}) => {
    const params = { page, size };
    if (strategyId) params.strategyId = strategyId;
    if (name) params.name = name;
    if (isPublic !== null && isPublic !== undefined) params.isPublic = isPublic;
    if (sort) params.sort = sort;
    return api.get('/strategies/search', { params });
  },

  /**
   * Get public strategies with pagination
   * GET /api/sat/algo/v1/strategies/public
   */
  getPublicStrategies: (page = 0, size = 20) =>
    api.get(`/strategies/public`, { params: { page, size } }),

  /**
   * Search public strategies with filters and pagination
   * GET /api/sat/algo/v1/strategies/public/search
   * @param {Object} params
   * @param {string} [params.strategyId] - Exact strategy ID match
   * @param {string} [params.name] - Case-insensitive partial name match
   * @param {number} [params.page=0] - Page number (zero-indexed)
   * @param {number} [params.size=20] - Page size
   */
  searchPublicStrategies: ({ strategyId, name, page = 0, size = 20 } = {}) => {
    const params = { page, size };
    if (strategyId) params.strategyId = strategyId;
    if (name) params.name = name;
    return api.get('/strategies/public/search', { params });
  },

  /**
   * Get a strategy by ID
   * GET /api/sat/algo/v1/strategies/trendline/{id}
   */
  getById: (id) => api.get(`${TRENDLINE_STRATEGY_BASE}/${id}`),

  /**
   * Create a new strategy with rule groups
   * POST /api/sat/algo/v1/strategies/trendline
   * @param {Object} strategy - Strategy object with ruleGroups
   */
  create: (strategy) => api.post(TRENDLINE_STRATEGY_BASE, strategy),

  /**
   * Update an existing strategy
   * PUT /api/sat/algo/v1/strategies/trendline/{id}
   */
  update: (id, strategy) => api.put(`${TRENDLINE_STRATEGY_BASE}/${id}`, strategy),

  /**
   * Delete a strategy
   * DELETE /api/sat/algo/v1/strategies/trendline/{id}
   */
  delete: (id) => api.delete(`${TRENDLINE_STRATEGY_BASE}/${id}`),

  // ============ Validation ============

  /**
   * Validate a strategy configuration without saving
   * POST /api/sat/algo/v1/strategies/trendline/validate
   * @returns {Object} { valid: boolean, errors: [], warnings: [], suggestions: [] }
   */
  validate: (strategy) => api.post(`${TRENDLINE_STRATEGY_BASE}/validate`, strategy),

  // ============ Indicator Schemas ============

  /**
   * Get parameter schemas for all supported indicators
   * GET /api/sat/algo/v1/strategies/trendline/indicators/schema
   * @returns {Object} { indicators: { RSI: {...}, SMA: {...}, EMA: {...}, MACD: {...} } }
   */
  getIndicatorSchemas: () => api.get(`${TRENDLINE_STRATEGY_BASE}/indicators/schema`),

  // ============ Rules Schema ============

  /**
   * Get all available rule types and operators for building strategies
   * GET /api/sat/algo/v1/strategies/trendline/rules/schema
   * @returns {Object} { ruleTypes: [...], comparisonOperators: [...], logicalOperators: [...] }
   */
  getRulesSchema: () => api.get(`${TRENDLINE_STRATEGY_BASE}/rules/schema`),

  // ============ Execution ============

  /**
   * Launch a strategy with specified execution mode (for LIVE/PAPER trading)
   * POST /api/sat/algo/v1/strategies/trendline/{strategyId}/launch
   * @param {string} strategyId - Strategy ID
   * @param {Object} launchParams - Launch parameters
   * @param {string} launchParams.broker - Broker to use (e.g., 'UPSTOX')
   * @param {'LIVE'|'PAPER'} launchParams.mode - Execution mode (default: 'PAPER')
   * @param {string} [launchParams.startDate] - Start date in ISO format (default: now)
   * @param {string} [launchParams.endDate] - End date in ISO format (default: 1 hour from now)
   * @returns {Object} { executionId, status, message }
   */
  launch: (strategyId, launchParams) => {
    const now = new Date();
    const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);

    const requestBody = {
      broker: launchParams.broker || 'UPSTOX',
      mode: launchParams.mode || 'PAPER',
      startDate: launchParams.startDate || now.toISOString(),
      endDate: launchParams.endDate || oneHourLater.toISOString(),
      interval: launchParams.interval,
    };

    return api.post(`${TRENDLINE_STRATEGY_BASE}/${strategyId}/launch`, requestBody);
  },

  /**
   * Get execution status for a strategy
   * GET /api/sat/algo/v1/strategies/trendline/{strategyId}/executions/{executionId}
   * @param {string} strategyId - Strategy ID
   * @param {string} executionId - Execution ID
   * @returns {Object} Execution details with status
   */
  getExecution: (strategyId, executionId) =>
    api.get(`${TRENDLINE_STRATEGY_BASE}/${strategyId}/executions/${executionId}`),

  /**
   * Stop a running execution
   * POST /api/sat/algo/v1/strategies/trendline/{strategyId}/executions/{executionId}/stop
   * @param {string} strategyId - Strategy ID
   * @param {string} executionId - Execution ID
   * @returns {Object} Updated execution details
   */
  stopExecution: (strategyId, executionId) =>
    api.post(`${TRENDLINE_STRATEGY_BASE}/${strategyId}/executions/${executionId}/stop`),

  /**
   * List all executions for a strategy
   * GET /api/sat/algo/v1/strategies/trendline/{strategyId}/executions
   * @param {string} strategyId - Strategy ID
   * @returns {Array} List of executions
   */
  listExecutions: (strategyId) =>
    api.get(`${TRENDLINE_STRATEGY_BASE}/${strategyId}/executions`),

  /**
   * Run backtest for a strategy (saves results to database)
   * POST /api/sat/algo/v1/strategies/trendline/{strategyId}/backtest
   * @param {string} strategyId - Strategy ID
   * @param {Object} params - Backtest parameters
   * @param {string} params.tickerName - Optional ticker override
   * @param {string} params.startDate - Start date (ISO format)
   * @param {string} params.endDate - End date (ISO format)
   * @param {string} params.interval - Candle interval (e.g., '1h', '1d')
   * @returns {Object} Backtest results with metrics
   */
  backtest: (strategyId, params = {}) =>
    api.post(`${TRENDLINE_STRATEGY_BASE}/${strategyId}/backtest`, params),
};

// ============ Rule Library Service ============
// Base: /api/sat/algo/v1/strategies/rules

const RULES_BASE = '/strategies/rules';

export const ruleLibraryService = {
  /**
   * List system rule templates with optional filtering
   * GET /api/sat/algo/v1/strategies/rules/templates
   */
  listTemplates: (category = null, indicatorType = null) =>
    api.get(`${RULES_BASE}/templates`, { params: { category, indicatorType } }),

  /**
   * Get a single rule template by ID
   * GET /api/sat/algo/v1/strategies/rules/templates/{id}
   */
  getTemplate: (id) => api.get(`${RULES_BASE}/templates/${id}`),

  /**
   * Search rule templates
   * GET /api/sat/algo/v1/strategies/rules/search?q={query}
   */
  searchTemplates: (q, category = null) =>
    api.get(`${RULES_BASE}/search`, { params: { q, category } }),

  /**
   * List available categories
   * GET /api/sat/algo/v1/strategies/rules/categories
   */
  getCategories: () => api.get(`${RULES_BASE}/categories`),

  /**
   * List all rules saved by the authenticated user
   * GET /api/sat/algo/v1/strategies/rules/user
   */
  listUserRules: () => api.get(`${RULES_BASE}/user`),

  /**
   * Get a specific user rule by ID
   * GET /api/sat/algo/v1/strategies/rules/user/{id}
   */
  getUserRule: (id) => api.get(`${RULES_BASE}/user/${id}`),

  /**
   * Create a new custom rule
   * POST /api/sat/algo/v1/strategies/rules/user
   */
  createUserRule: (ruleData) => api.post(`${RULES_BASE}/user`, ruleData),

  /**
   * Update an existing user rule
   * PUT /api/sat/algo/v1/strategies/rules/user/{id}
   */
  updateUserRule: (id, ruleData) => api.put(`${RULES_BASE}/user/${id}`, ruleData),

  /**
   * Delete a user rule
   * DELETE /api/sat/algo/v1/strategies/rules/user/{id}
   */
  deleteUserRule: (id) => api.delete(`${RULES_BASE}/user/${id}`),

  /**
   * Clone a system template to the user's library
   * POST /api/sat/algo/v1/strategies/rules/templates/{id}/clone
   */
  cloneTemplate: (id, cloneParams = {}) =>
    api.post(`${RULES_BASE}/templates/${id}/clone`, cloneParams),
};

// ============ Stock Search Service ============
// Base: /api/sat/algo/v1/stocks

const STOCK_SEARCH_BASE = '/stocks';

/**
 * Stock search service for ticker symbol autocomplete and historical data
 */
export const stockSearchService = {
  /**
   * Search for stocks by query string
   * GET /api/sat/algo/v1/stocks/search?q={query}
   * @param {string} query - Search query (e.g., "RELIANCE", "TCS")
   * @returns {Array} List of matching stocks
   */
  search: (query) => api.get(`${STOCK_SEARCH_BASE}/search`, { params: { q: query } }),

  /**
   * Get historical candle data for a stock
   * GET /api/sat/algo/v1/stocks/{symbol}/history?from={from}&to={to}&interval={interval}
   * @param {string} symbol - Stock symbol (e.g., "TITAN", "RELIANCE")
   * @param {string} from - Start date in ISO-8601 format (e.g., "2025-01-01T00:00:00Z")
   * @param {string} to - End date in ISO-8601 format
   * @param {string} interval - Candle interval: 1m, 5m, 15m, 30m, 1h, 1d, 1w, 1M (default: 1h)
   * @param {string} exchange - Exchange: NSE, BSE (optional)
   * @param {string} instrumentType - Instrument type: EQ (default: EQ)
   * @returns {Array} List of candle data { timestamp, open, high, low, close, volume }
   */
  getHistory: (symbol, from, to, interval = '1h', exchange = 'NSE', instrumentType = 'EQ') =>
    api.get(`${STOCK_SEARCH_BASE}/${symbol}/history`, {
      params: { from, to, interval, exchange, instrumentType }
    }),

  /**
   * Get the SSE stream URL for live market data
   * This URL is used with EventSource for real-time updates
   * @param {string} symbol - Stock symbol (e.g., "RELIANCE", "INFY")
   * @param {string} exchange - Exchange: NSE, BSE (default: NSE)
   * @param {string} instrumentType - Instrument type: EQ (default: EQ)
   * @returns {string} Full URL for SSE connection
   */
  getStreamUrl: (symbol, exchange = 'NSE', instrumentType = 'EQ') => {
    const params = new URLSearchParams({ exchange, instrumentType });
    return `${VERSIONED_API_URL}${STOCK_SEARCH_BASE}/${encodeURIComponent(symbol)}/stream?${params.toString()}`;
  },
};

// ============ Type Definitions (for reference) ============

/**
 * @typedef {Object} TrendLine
 * @property {string} name - Display name for the trend line
 * @property {string} id - Unique identifier
 * @property {number} x1 - Start timestamp (epoch milliseconds)
 * @property {number} y1 - Start price
 * @property {number} x2 - End timestamp (epoch milliseconds)
 * @property {number} y2 - End price
 */

/**
 * @typedef {Object} RuleCondition
 * @property {'TRENDLINE'|'INDICATOR'} conditionType
 * @property {TrendLine} [trendLine] - Required if conditionType is TRENDLINE
 * @property {'PRICE_CROSSOVER_TRENDLINE'|'PRICE_CROSSUNDER_TRENDLINE'|'PRICE_TOUCHED_TRENDLINE'} [triggerType]
 * @property {'RSI'|'SMA'|'EMA'|'MACD'} [indicatorType] - Required if conditionType is INDICATOR
 * @property {'GREATER_THAN'|'LESS_THAN'|'CROSSED_UP'|'CROSSED_DOWN'|'EQUALS'} [operator]
 * @property {Object} [parameters] - Indicator-specific parameters
 */

/**
 * @typedef {Object} RuleGroup
 * @property {string} groupId - Unique identifier for the group
 * @property {string} [groupName] - Display name
 * @property {'AND'|'OR'} operator - How conditions are combined
 * @property {'BUY'|'SELL'|'NONE'} action - Action to take when conditions are met
 * @property {RuleCondition[]} conditions - List of conditions
 */

/**
 * @typedef {Object} TrendlineStrategy
 * @property {string} [id] - Strategy ID (set by server)
 * @property {string} name - Strategy name
 * @property {string} [description] - Strategy description
 * @property {string} tickerName - Stock ticker symbol
 * @property {string} [startDate] - ISO date string (e.g., "2025-01-01")
 * @property {string} [endDate] - ISO date string
 * @property {RuleGroup[]} ruleGroups - Array of rule groups
 */

// ============ Constants for UI ============

export const CONDITION_TYPES = {
  TRENDLINE: 'TRENDLINE',
  INDICATOR: 'INDICATOR',
};

export const TRIGGER_TYPES = {
  PRICE_CROSSOVER_TRENDLINE: { value: 'PRICE_CROSSOVER_TRENDLINE', label: 'Price Crosses Above' },
  PRICE_CROSSUNDER_TRENDLINE: { value: 'PRICE_CROSSUNDER_TRENDLINE', label: 'Price Crosses Below' },
  PRICE_TOUCHED_TREND_LINE: { value: 'PRICE_TOUCHED_TREND_LINE', label: 'Price Touches' },
};

export const INDICATOR_TYPES = {
  RSI: { value: 'RSI', label: 'Relative Strength Index (RSI)' },
  SMA: { value: 'SMA', label: 'Simple Moving Average (SMA)' },
  EMA: { value: 'EMA', label: 'Exponential Moving Average (EMA)' },
  WMA: { value: 'WMA', label: 'Weighted Moving Average (WMA)' },
  MACD: { value: 'MACD', label: 'MACD' },
  BOLLINGER_UPPER: { value: 'BOLLINGER_UPPER', label: 'Bollinger Upper Band' },
  BOLLINGER_LOWER: { value: 'BOLLINGER_LOWER', label: 'Bollinger Lower Band' },
  BOLLINGER_MIDDLE: { value: 'BOLLINGER_MIDDLE', label: 'Bollinger Middle Band' },
  ATR: { value: 'ATR', label: 'Average True Range (ATR)' },
  STOCHASTIC_K: { value: 'STOCHASTIC_K', label: 'Stochastic %K' },
  STOCHASTIC_D: { value: 'STOCHASTIC_D', label: 'Stochastic %D' },
  CCI: { value: 'CCI', label: 'Commodity Channel Index (CCI)' },
  WILLIAMS_R: { value: 'WILLIAMS_R', label: 'Williams %R' },
  CLOSE: { value: 'CLOSE', label: 'Close Price' },
};

export const COMPARISON_OPERATORS = {
  GREATER_THAN: { value: 'GREATER_THAN', label: 'Greater Than (>)' },
  LESS_THAN: { value: 'LESS_THAN', label: 'Less Than (<)' },
  CROSSED_UP: { value: 'CROSSED_UP', label: 'Crossed Above' },
  CROSSED_DOWN: { value: 'CROSSED_DOWN', label: 'Crossed Below' },
  EQUALS: { value: 'EQUALS', label: 'Equals (=)' },
};

export const LOGICAL_OPERATORS = {
  AND: { value: 'AND', label: 'AND (All conditions must be true)' },
  OR: { value: 'OR', label: 'OR (Any condition must be true)' },
};

export const ACTIONS = {
  BUY: { value: 'BUY', label: 'Buy (Entry Signal)' },
  SELL: { value: 'SELL', label: 'Sell (Exit Signal)' },
  NONE: { value: 'NONE', label: 'None (Monitor Only)' },
};

// ============ Broker Service ============
// Base: /api/sat/algo/v1/brokers

export const brokerService = {
  /**
   * Get all onboarded brokers for the user
   * GET /api/sat/algo/v1/brokers/onboarded
   */
  getOnboardedBrokers: () => api.get('/brokers/onboarded'),

  /**
   * Get all supported brokers
   * GET /api/sat/algo/v1/brokers/supported
   */
  getSupportedBrokers: () => api.get('/brokers/supported'),

  /**
   * Onboard a broker
   * POST /api/sat/algo/v1/brokers/onboard/{brokerId}
   */
  onboardBroker: (brokerId, data) => api.post(`/brokers/onboard/${brokerId}`, data),

  /**
   * Get broker login URL (OAuth flow)
   * GET /api/sat/algo/v1/brokers/{brokerName}/login
   * Note: This endpoint triggers OAuth redirect, handled by broker-specific controllers
   */
  connectBroker: (brokerName) => api.get(`/brokers/${brokerName.toLowerCase()}/login`),

  /**
   * Remove a broker
   * DELETE /api/sat/algo/v1/brokers/{brokerId}
   */
  removeBroker: (brokerId) => api.delete(`/brokers/${brokerId}`),

  /**
   * Get broker onboarding config
   * GET /api/sat/algo/v1/brokers/onboard/{brokerId}
   */
  getBrokerOnboardingConfig: (brokerId) => api.get(`/brokers/onboard/${brokerId}`),

  /**
   * Manual broker onboarding with credentials
   * PUT /api/sat/algo/v1/brokers/onboard/{brokerId}
   */
  onboardBrokerManual: (brokerId, data) => api.put(`/brokers/onboard/${brokerId}`, data),
};

export const tradingViewConfig = {
  chartsStorageUrl: 'https://saveload.tradingview.com',
  scriptSrc: 'https://s3.tradingview.com/tv.js',
};

// ============ Execution Service (Live Trading) ============
// Base: /api/sat/algo/v1/executions

const EXECUTION_BASE = '/executions';

/**
 * Execution service for managing live strategy executions
 */
export const executionService = {
  /**
   * Start a new execution
   * POST /api/sat/algo/v1/executions
   * @param {Object} data - { strategyId, broker, startDate?, endDate? }
   */
  start: (data) => api.post(EXECUTION_BASE, data),

  /**
   * List all executions for the user
   * GET /api/sat/algo/v1/executions?status={status}
   * @param {string} status - Optional status filter
   */
  list: (status = null) => {
    const params = status ? { status } : {};
    return api.get(EXECUTION_BASE, { params });
  },

  /**
   * Get execution details
   * GET /api/sat/algo/v1/executions/{id}
   */
  get: (executionId) => api.get(`${EXECUTION_BASE}/${executionId}`),

  /**
   * Stop an execution
   * POST /api/sat/algo/v1/executions/{id}/stop
   */
  stop: (executionId) => api.post(`${EXECUTION_BASE}/${executionId}/stop`),

  /**
   * Get orders for an execution
   * GET /api/sat/algo/v1/executions/{id}/orders
   */
  getOrders: (executionId, page = 0, size = 50) =>
    api.get(`${EXECUTION_BASE}/${executionId}/orders`, { params: { page, size } }),

  /**
   * Get signals for an execution
   * GET /api/sat/algo/v1/executions/{id}/signals
   */
  getSignals: (executionId, page = 0, size = 50) =>
    api.get(`${EXECUTION_BASE}/${executionId}/signals`, { params: { page, size } }),

  /**
   * Get positions for an execution
   * GET /api/sat/algo/v1/executions/{id}/positions
   */
  getPositions: (executionId) => api.get(`${EXECUTION_BASE}/${executionId}/positions`),

  /**
   * Get P&L summary for an execution
   * GET /api/sat/algo/v1/executions/{id}/pnl
   */
  getPnl: (executionId) => api.get(`${EXECUTION_BASE}/${executionId}/pnl`),

  /**
   * Get lifecycle events for an execution (for timeline visualization)
   * GET /api/sat/algo/v1/executions/{id}/events
   * @param {string} executionId - Execution ID
   * @param {string} component - Optional component filter (e.g., 'CONTROLLER', 'WORKER')
   */
  getEvents: (executionId, component = null) => {
    const params = component ? { component } : {};
    return api.get(`${EXECUTION_BASE}/${executionId}/events`, { params });
  },
};

// ============ Backtest Service ============
// Base: /api/sat/algo/v1/backtests

const BACKTEST_BASE = '/backtests';

/**
 * Backtest service for managing backtest history and results
 */
export const backtestService = {
  /**
   * Get all backtests for the authenticated user
   * GET /api/sat/algo/v1/backtests/history
   * @returns {Array} List of backtest results
   */
  getHistory: () => api.get(`${BACKTEST_BASE}/history`),

  /**
   * Get a specific backtest by ID
   * GET /api/sat/algo/v1/backtests/{id}
   * @param {string} id - Backtest ID
   * @returns {Object} Backtest result with full details
   */
  getById: (id) => api.get(`${BACKTEST_BASE}/${id}`),

  /**
   * Get all backtests for a specific strategy
   * GET /api/sat/algo/v1/backtests/strategy/{strategyId}
   * @param {string} strategyId - Strategy ID
   * @returns {Array} List of backtest results for the strategy
   */
  getByStrategy: (strategyId) => api.get(`${BACKTEST_BASE}/strategy/${strategyId}`),

  /**
   * Delete a backtest result
   * DELETE /api/sat/algo/v1/backtests/{id}
   * @param {string} id - Backtest ID
   */
  delete: (id) => api.delete(`${BACKTEST_BASE}/${id}`),

  /**
   * Get all series data for charts (equity, returns, price, exposure)
   * GET /api/sat/algo/v1/backtests/{id}/series
   * @param {string} id - Backtest ID
   * @returns {Object} Series data with equitySeries, returnsSeries, priceSeries, exposureSeries
   */
  getSeriesData: (id) => api.get(`${BACKTEST_BASE}/${id}/series`),

  /**
   * Get specific series type for charts
   * GET /api/sat/algo/v1/backtests/{id}/series/{type}
   * @param {string} id - Backtest ID
   * @param {string} type - One of: equity, returns, price, exposure
   * @returns {Array} Series data for the specified type
   */
  getSeriesByType: (id, type) => api.get(`${BACKTEST_BASE}/${id}/series/${type}`),
};

// ============ Backtest Job Service (Async) ============
// Base: /api/sat/algo/v1/backtests/jobs

const BACKTEST_JOB_BASE = '/backtests/jobs';

/**
 * Backtest job service for async backtest management.
 * Use this for long-running backtests with large historical data.
 */
export const backtestJobService = {
  /**
   * Submit a new async backtest job
   * POST /api/sat/algo/v1/backtests/jobs?strategyId={id}&strategyName={name}
   * @param {string} strategyId - Strategy ID
   * @param {string} strategyName - Strategy name
   * @param {Object} params - Backtest parameters (tickerName, startDate, endDate, interval, etc.)
   * @returns {Object} Job with status PENDING
   */
  submit: (strategyId, strategyName, params) =>
    api.post(`${BACKTEST_JOB_BASE}?strategyId=${encodeURIComponent(strategyId)}&strategyName=${encodeURIComponent(strategyName)}`, params),

  /**
   * List all jobs for the authenticated user
   * GET /api/sat/algo/v1/backtests/jobs
   * @returns {Array} List of jobs ordered by creation time (newest first)
   */
  list: () => api.get(BACKTEST_JOB_BASE),

  /**
   * Search backtest jobs with filters and pagination
   * GET /api/sat/algo/v1/backtests/jobs/search
   * @param {Object} params
   * @param {string} [params.jobId] - Exact job ID match
   * @param {string} [params.strategyName] - Case-insensitive partial match
   * @param {string} [params.status] - PENDING, RUNNING, COMPLETED, FAILED, CANCELLED
   * @param {number} [params.page=0] - Page number (zero-indexed)
   * @param {number} [params.size=20] - Page size
   */
  searchJobs: ({ jobId, strategyName, status, page = 0, size = 20 } = {}) => {
    const params = { page, size };
    if (jobId) params.jobId = jobId;
    if (strategyName) params.strategyName = strategyName;
    if (status) params.status = status;
    return api.get(`${BACKTEST_JOB_BASE}/search`, { params });
  },

  /**
   * Get a specific job by ID
   * GET /api/sat/algo/v1/backtests/jobs/{id}
   * @param {string} jobId - Job ID
   * @returns {Object} Job details with current status and progress
   */
  get: (jobId) => api.get(`${BACKTEST_JOB_BASE}/${jobId}`),

  /**
   * Get job progress (lightweight polling endpoint)
   * GET /api/sat/algo/v1/backtests/jobs/{id}/progress
   * @param {string} jobId - Job ID
   * @returns {Object} { jobId, status, progress, currentChunk, totalChunks, resultId, errorMessage }
   */
  getProgress: (jobId) => api.get(`${BACKTEST_JOB_BASE}/${jobId}/progress`),

  /**
   * Cancel a running job
   * DELETE /api/sat/algo/v1/backtests/jobs/{id}
   * @param {string} jobId - Job ID
   * @returns {Object} Updated job with CANCELLED status
   */
  cancel: (jobId) => api.delete(`${BACKTEST_JOB_BASE}/${jobId}`),

  /**
   * Get series data for a completed job's backtest result
   * GET /api/sat/algo/v1/backtests/jobs/{id}/series
   * @param {string} jobId - Job ID
   * @returns {Object} Series data (equity, returns)
   */
  getSeriesData: (jobId) => api.get(`${BACKTEST_JOB_BASE}/${jobId}/series`),

  /**
   * Get price series for a completed job
   * GET /api/sat/algo/v1/backtests/jobs/{id}/series/price
   * @param {string} jobId - Job ID
   * @returns {Array} Price series data
   */
  getPriceSeries: (jobId) => api.get(`${BACKTEST_JOB_BASE}/${jobId}/series/price`),
};


// ============ Agent Service ============
// Base: /api/agent (different from main API base)

// Agent API uses a different base path than the main SAT algo API
// In development, use relative URL so setupProxy.js can handle routing
// In production, set REACT_APP_SAT_AGENTS_URL to the absolute URL
const getAgentApiBase = () => {
  const configuredUrl = process.env.REACT_APP_SAT_AGENTS_URL;

  // If SAT_AGENTS specific URL is set, use it
  if (configuredUrl) {
    return configuredUrl;
  }

  // In development, use relative URL so proxy handles it
  // The setupProxy.js will route /api/agent to localhost:8090
  return '';
};

const AGENT_API_URL = `${getAgentApiBase()}/api/agent`;

/**
 * Agent service for SAT AI agents
 */
export const agentService = {
  /**
   * Get all available agents
   * GET /api/agent/list
   * @returns {Array} List of available agents with metadata
   */
  getAvailableAgents: async () => {
    const response = await fetch(`${AGENT_API_URL}/list`);
    if (!response.ok) {
      throw new Error('Failed to fetch agents');
    }
    return response.json();
  },
};

// ============ Helper Functions ============

/**
 * Get the agent API base URL
 * @returns {string} Agent API URL (e.g., "/api/agent" for dev, or full URL for production)
 */
export const getAgentApiUrl = () => AGENT_API_URL;

/**
 * Get the WebSocket URL for agent connections
 * Note: We connect directly to the backend for WebSocket because Create React App's
 * dev server proxy doesn't properly handle WebSocket upgrade requests.
 * @returns {string} WebSocket URL (e.g., "ws://localhost:8090/ws/agent")
 */
export const getWebSocketUrl = () => {
  const configuredUrl = process.env.REACT_APP_SAT_AGENTS_URL;

  // If SAT_AGENTS specific URL is set, use it
  if (configuredUrl) {
    const wsProtocol = configuredUrl.startsWith('https://') ? 'wss:' : 'ws:';
    const host = configuredUrl.replace(/^https?:\/\//, '');
    return `${wsProtocol}//${host}/ws/agent`;
  }

  // In development, connect directly to backend (port 8090)
  // CRA dev server proxy doesn't properly forward WebSocket upgrades
  return 'ws://localhost:8090/ws/agent';
};

/**
 * Get the WebSocket URL for strategy orchestrator connections.
 * @returns {string} WebSocket URL for strategy (e.g., "ws://localhost:8090/ws/strategy")
 */
export const getStrategyWebSocketUrl = () => {
  const configuredUrl = process.env.REACT_APP_SAT_AGENTS_URL;

  if (configuredUrl) {
    const wsProtocol = configuredUrl.startsWith('https://') ? 'wss:' : 'ws:';
    const host = configuredUrl.replace(/^https?:\/\//, '');
    return `${wsProtocol}//${host}/ws/strategy`;
  }

  return 'ws://localhost:8090/ws/strategy';
};

/**
 * Get the WebSocket URL for code-explorer agent connections.
 * @returns {string} WebSocket URL for code-explorer (e.g., "ws://localhost:8090/ws/code-explorer")
 */
export const getCodeExplorerWebSocketUrl = () => {
  const configuredUrl = process.env.REACT_APP_SAT_AGENTS_URL;

  if (configuredUrl) {
    const wsProtocol = configuredUrl.startsWith('https://') ? 'wss:' : 'ws:';
    const host = configuredUrl.replace(/^https?:\/\//, '');
    return `${wsProtocol}//${host}/ws/code-explorer`;
  }

  return 'ws://localhost:8090/ws/code-explorer';
};

/**
 * Get the WebSocket URL for debugger agent connections.
 * @returns {string} WebSocket URL for debugger (e.g., "ws://localhost:8090/ws/debugger")
 */
export const getDebuggerWebSocketUrl = () => {
  const configuredUrl = process.env.REACT_APP_SAT_AGENTS_URL;

  if (configuredUrl) {
    const wsProtocol = configuredUrl.startsWith('https://') ? 'wss:' : 'ws:';
    const host = configuredUrl.replace(/^https?:\/\//, '');
    return `${wsProtocol}//${host}/ws/debugger`;
  }

  return 'ws://localhost:8090/ws/debugger';
};

/**
 * Get WebSocket URL for any agent by name.
 * @param {string} agentName - Agent name (e.g., 'strategy-orchestrator', 'code-explorer')
 * @returns {string} WebSocket URL for the specified agent
 */
export const getAgentWebSocketUrl = (agentName) => {
  switch (agentName) {
    case 'strategy-orchestrator':
      return getStrategyWebSocketUrl();
    case 'code-explorer':
      return getCodeExplorerWebSocketUrl();
    case 'debugger':
      return getDebuggerWebSocketUrl();
    default:
      return getWebSocketUrl();
  }
};

// ============ Session Service ============

/**
 * Session service for managing chat sessions
 */
export const sessionService = {
  /**
   * Get all sessions for the authenticated user
   * GET /api/agent/sessions
   */
  listSessions: async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${AGENT_API_URL}/sessions`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch sessions');
    return response.json();
  },

  /**
   * Get session details with conversation history
   * GET /api/agent/sessions/{sessionId}
   */
  getSession: async (sessionId) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${AGENT_API_URL}/sessions/${sessionId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch session');
    return response.json();
  },

  /**
   * Delete a session
   * DELETE /api/agent/sessions/{sessionId}
   */
  deleteSession: async (sessionId) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${AGENT_API_URL}/sessions/${sessionId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to delete session');
  },

  /**
   * Get formatted chat messages for a session (user + assistant messages).
   * GET /api/sessions/{sessionId}/messages
   * @param {string} sessionId - Session ID
   * @returns {Object} { sessionId, totalMessages, messages: [{ role, content, type?, timestamp? }] }
   */
  getSessionMessages: async (sessionId) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${getAgentApiBase()}/api/sessions/${sessionId}/messages`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch session messages');
    return response.json();
  }
};

// ============ PSM Session Service (Persistent Session Management) ============
// Base: /api/agent/psm (session lifecycle with S3 archive/restore)

const PSM_API_URL = `${getAgentApiBase()}/api/agent/psm`;

/**
 * Persistent Session Management service for session lifecycle with S3 archive/restore.
 * Manages ACTIVE and ARCHIVED sessions across agents.
 */
export const psmSessionService = {
  /**
   * List all sessions (ACTIVE + ARCHIVED) for the authenticated user.
   * GET /api/agent/psm/sessions?agentName={agentName}
   * @param {string} [agentName] - Optional agent name filter
   * @returns {Object} { sessions: [...] }
   */
  listSessions: async (agentName) => {
    const token = localStorage.getItem('token');
    const params = agentName ? `?agentName=${encodeURIComponent(agentName)}` : '';
    const response = await fetch(`${PSM_API_URL}/sessions${params}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch PSM sessions');
    return response.json();
  },

  /**
   * Get session details.
   * GET /api/agent/psm/sessions/{sessionId}
   * @param {string} sessionId - Session ID
   * @returns {Object} SessionDocument
   */
  getSession: async (sessionId) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${PSM_API_URL}/sessions/${sessionId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch session');
    return response.json();
  },

  /**
   * Restore an archived session from S3.
   * POST /api/agent/psm/sessions/{sessionId}/restore
   * @param {string} sessionId - Archived session ID to restore
   * @returns {Object} { restoredSessionId, status, eventCount, message }
   */
  restoreSession: async (sessionId) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${PSM_API_URL}/sessions/${sessionId}/restore`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to restore session');
    }
    return response.json();
  },

  /**
   * Manually archive an active session to S3.
   * POST /api/agent/psm/sessions/{sessionId}/archive
   * @param {string} sessionId - Active session ID to archive
   * @returns {Object} { sessionId, status, message }
   */
  archiveSession: async (sessionId) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${PSM_API_URL}/sessions/${sessionId}/archive`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to archive session');
    }
    return response.json();
  },
};

// ============ AI Usage Service ============

/**
 * AI Usage service for tracking token usage and billing
 * Base path: /api/agent/usage
 */
export const aiUsageService = {
  /**
   * Get total usage summary for the authenticated user
   * GET /api/agent/usage/user
   * @returns {Object} TokenUsageSummary
   */
  getUserUsage: async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${AGENT_API_URL}/usage/user`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch usage summary');
    return response.json();
  },

  /**
   * Get billing summary with model and agent breakdown
   * GET /api/agent/usage/user/billing?startEpochMs=&endEpochMs=
   * @param {number} [startEpochMs] - Start of billing period (defaults to 30 days ago on server)
   * @param {number} [endEpochMs] - End of billing period (defaults to now on server)
   * @returns {Object} UserBillingSummary
   */
  getBilling: async (startEpochMs, endEpochMs) => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    if (startEpochMs) params.append('startEpochMs', startEpochMs);
    if (endEpochMs) params.append('endEpochMs', endEpochMs);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await fetch(`${AGENT_API_URL}/usage/user/billing${query}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch billing data');
    return response.json();
  },

  /**
   * Get recent usage records
   * GET /api/agent/usage/user/recent?limit=50
   * @param {number} [limit=50] - Number of records to fetch
   * @returns {Array} List of TokenUsageRecord
   */
  getRecentUsage: async (limit = 50) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${AGENT_API_URL}/usage/user/recent?limit=${limit}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch recent usage');
    return response.json();
  },
};


// ============ Exports ============

// Export utilities for components that need direct access
export { ApiError, extractApiError, getErrorMessage } from '../utils/apiError';
export { extractPagination, hasMorePages } from '../utils/apiResponse';

export default api;

